/* CAVE — product gallery (swipe, arrows, thumbnails, zoom) and click-to-play
   videos. Works with Arabic (RTL) scrolling, keyboard and touch. Nothing heavy
   loads until it is needed: lightbox images load when opened, videos only
   when the shopper presses play. */

const isRTL = (el) => getComputedStyle(el).direction === 'rtl';

class CaveGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.prev = this.querySelector('.cave-gallery__nav[data-prev]');
    this.next = this.querySelector('.cave-gallery__nav[data-next]');
    this.badge = this.querySelector('[data-badge]');
    this.dialog = this.querySelector('[data-lightbox]');
    this.index = 0;
    if (!this.track || !this.slides.length) return;

    this.io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) this.setIndex(this.slides.indexOf(e.target)); });
    }, { root: this.track, threshold: 0.6 });
    this.slides.forEach((s) => this.io.observe(s));

    this.prev?.addEventListener('click', () => this.go(this.index - 1));
    this.next?.addEventListener('click', () => this.go(this.index + 1));
    this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.thumb))));
    this.slides.forEach((s, i) => s.querySelector('[data-zoom]')?.addEventListener('click', () => this.open(i)));
    this.track.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const forward = (e.key === 'ArrowLeft') === isRTL(this);
      this.go(this.index + (forward ? 1 : -1));
      e.preventDefault();
    });
    this.setupLightbox();
  }
  disconnectedCallback() { this.io?.disconnect(); }

  go(i, behavior = 'smooth') {
    const n = this.slides.length;
    const target = Math.max(0, Math.min(n - 1, i));
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) behavior = 'instant';
    const sign = isRTL(this.track) ? -1 : 1;
    this.track.scrollTo({ left: sign * target * this.track.clientWidth, behavior });
    this.setIndex(target);
  }
  setIndex(i) {
    if (i < 0) return;
    this.index = i;
    const n = this.slides.length;
    if (this.badge) this.badge.textContent = `${i + 1} / ${n}`;
    if (this.prev) this.prev.disabled = i === 0;
    if (this.next) this.next.disabled = i === n - 1;
    this.thumbs.forEach((t, k) => {
      t.setAttribute('aria-current', String(k === i));
      // Scroll only the thumbnail rail; scrollIntoView can move the whole page.
      if (k === i && t.parentElement) {
        const rail = t.parentElement;
        const a = t.getBoundingClientRect(), b = rail.getBoundingClientRect();
        if (a.left < b.left || a.right > b.right) rail.scrollBy({ left: a.left - b.left - (b.width - a.width) / 2, behavior: 'auto' });
      }
    });
  }

  /* ---------- Lightbox ---------- */
  setupLightbox() {
    const d = this.dialog;
    if (!d) return;
    this.lbImg = d.querySelector('[data-lb-img]');
    this.lbCount = d.querySelector('[data-lb-count]');
    this.stage = d.querySelector('[data-stage]');
    d.querySelector('[data-close]')?.addEventListener('click', () => d.close());
    d.querySelector('[data-prev]')?.addEventListener('click', () => this.show(this.lbIndex - 1));
    d.querySelector('[data-next]')?.addEventListener('click', () => this.show(this.lbIndex + 1));
    d.addEventListener('close', () => { this.unzoom(); this.go(this.lbIndex, 'instant'); });
    d.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const forward = (e.key === 'ArrowLeft') === isRTL(this);
      this.show(this.lbIndex + (forward ? 1 : -1));
    });
    // Tap toggles zoom at the tapped point; when zoomed, the picture pans by scrolling
    this.lbImg.addEventListener('click', (e) => {
      if (this.moved) return;
      if (d.classList.contains('is-zoomed')) { this.unzoom(); return; }
      const r = this.lbImg.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width;
      const py = (e.clientY - r.top) / r.height;
      const w = Math.max(this.stage.clientWidth * 2.2, r.width * 2.2);
      this.lbImg.style.inlineSize = `${w}px`;
      d.classList.add('is-zoomed');
      requestAnimationFrame(() => {
        const h = w * (this.lbImg.naturalHeight / this.lbImg.naturalWidth || r.height / r.width);
        this.stage.scrollLeft = px * w - this.stage.clientWidth / 2;
        this.stage.scrollTop = py * h - this.stage.clientHeight / 2;
      });
    });
    // Swipe between pictures when not zoomed
    let x0 = null;
    this.stage.addEventListener('pointerdown', (e) => { x0 = e.clientX; this.moved = false; });
    this.stage.addEventListener('pointermove', (e) => { if (x0 !== null && Math.abs(e.clientX - x0) > 8) this.moved = true; });
    this.stage.addEventListener('pointerup', (e) => {
      if (x0 === null) return;
      const dx = e.clientX - x0; x0 = null;
      if (d.classList.contains('is-zoomed') || Math.abs(dx) < 50) return;
      const forward = isRTL(this) ? dx > 0 : dx < 0;
      this.show(this.lbIndex + (forward ? 1 : -1));
    });
  }
  open(i) {
    if (!this.dialog) return;
    this.show(i);
    this.dialog.showModal();
  }
  show(i) {
    const n = this.slides.length;
    this.lbIndex = (i + n) % n;
    const slide = this.slides[this.lbIndex];
    this.unzoom();
    this.lbImg.src = slide.dataset.full;
    this.lbImg.alt = slide.querySelector('img')?.alt || '';
    if (this.lbCount) this.lbCount.textContent = `${this.lbIndex + 1} / ${n}`;
  }
  unzoom() {
    this.dialog?.classList.remove('is-zoomed');
    if (this.lbImg) this.lbImg.style.inlineSize = '';
    if (this.stage) { this.stage.scrollTop = 0; this.stage.scrollLeft = 0; }
  }
}

/* ---------- Click-to-play video ---------- */
class CavePlayer extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-play]');
    this.template = this.querySelector('template');
    this.button?.addEventListener('click', () => this.play());
    this.details = this.closest('details');
    this.onToggle = () => { if (this.details && !this.details.open) this.video?.pause(); };
    this.details?.addEventListener('toggle', this.onToggle);
    if (this.dataset.autoplay === 'true' && !this.details) {
      this.autoObserver = new IntersectionObserver(([entry]) => {
        this.isVisible = entry.isIntersecting;
        if (entry.isIntersecting && !this.manualPause && !matchMedia('(prefers-reduced-motion: reduce)').matches) this.play(true);
        else this.video?.pause();
      }, {threshold: .2});
      this.autoObserver.observe(this);
    }
  }
  disconnectedCallback() { this.autoObserver?.disconnect(); this.details?.removeEventListener('toggle', this.onToggle); this.video?.pause(); }
  play(automatic = false) {
    if (!this.video && this.template) {
      this.append(this.template.content.cloneNode(true));
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.video.controls = true;
      this.video.playsInline = true;
      this.video.preload = 'auto';
      this.video.muted = automatic;
      this.video.loop = automatic;
      if (this.dataset.autoplay === 'true') {
        this.sound = document.createElement('button');
        this.sound.type = 'button'; this.sound.dataset.sound = '';
        this.sound.textContent = this.dataset.soundOn || 'تشغيل الصوت';
        this.sound.setAttribute('aria-label', this.sound.textContent);
        this.sound.addEventListener('click', () => {
          this.video.muted = !this.video.muted;
        });
        this.video.addEventListener('volumechange', () => {
          this.sound.textContent = this.video.muted ? (this.dataset.soundOn || 'تشغيل الصوت') : (this.dataset.soundOff || 'كتم الصوت');
          this.sound.setAttribute('aria-label', this.sound.textContent);
        });
        this.append(this.sound);
      }
      this.video.addEventListener('play', () => {
        this.manualPause = false;
        document.querySelectorAll('cave-player video').forEach((v) => { if (v !== this.video) v.pause(); });
      });
      this.video.addEventListener('pause', () => {
        if (this.isVisible) this.manualPause = true;
      });
    }
    if (!this.video) return;
    this.classList.add('is-playing');
    const p = this.video?.play();
    p?.catch(() => {}); // controls stay available if the browser refuses
    if (!automatic) this.video?.focus({ preventScroll: true });
  }
}

const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
define('cave-gallery', CaveGallery);
define('cave-player', CavePlayer);


class CaveAmbient extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;
    this.video.muted = true;
    this.video.playsInline = true;
    const sound = this.querySelector('[data-sound]');
    sound?.addEventListener('click', () => {
      this.video.muted = !this.video.muted;
      sound.textContent = this.video.muted ? (this.dataset.soundOn || 'تشغيل الصوت') : (this.dataset.soundOff || 'كتم الصوت');
      sound.setAttribute('aria-label', sound.textContent);
      this.video.play().catch(() => {});
    });
    const pause = this.querySelector('[data-pause]');
    pause?.addEventListener('click', () => {
      const wasPaused = this.video.paused;
      this.manualPause = !wasPaused;
      if (wasPaused) this.video.play().catch(() => {}); else this.video.pause();
      pause.textContent = wasPaused ? (this.dataset.pauseLabel || 'إيقاف الفيديو') : (this.dataset.resumeLabel || 'تشغيل الفيديو');
      pause.setAttribute('aria-label', pause.textContent);
    });
    this.io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !this.manualPause && !matchMedia('(prefers-reduced-motion: reduce)').matches) this.video.play().catch(() => {});
      else this.video.pause();
    }, {threshold: .15});
    this.io.observe(this);
  }
  disconnectedCallback() {this.io?.disconnect(); this.video?.pause();}
}
define('cave-ambient', CaveAmbient);

